import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from './store';
import apiClient from '../apiClient';
import { mapApiAddressToState, unwrapAddressEntity, unwrapAddressList, resolveAddressCity } from '../../utils/addressDisplay';

export interface Address {
  id: string;
  label: string;
  fullName: string;
  phone: string;
  addressLine: string;
  city: string;
  state: string;
  zip: string;
  lat?: string;
  long?: string;
}

export interface AddressState {
  addresses: Address[];
  selectedAddressId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: AddressState = {
  addresses: [],
  selectedAddressId: null,
  loading: false,
  error: null,
};

// Async thunk for creating an address
export const createAddress = createAsyncThunk<
  Address,
  {
    address_line_1: string;
    user_id: string;
    address_line_2?: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
    address_type: string;
    location: { lat: string; long: string };
    name: string;
    phoneNumber: string;
  },
  { rejectValue: string }
>(
  'address/createAddress',
  async (payload, { rejectWithValue }) => {
    try {
      const normalized = {
        ...payload,
        city: resolveAddressCity({
          city: payload.city,
          address_line_1: payload.address_line_1,
        }) || payload.city,
        address_line_2: payload.address_line_2 && String(payload.address_line_2).trim() ? payload.address_line_2 : '-',
        state: payload.state && String(payload.state).trim() ? payload.state : '-',
      };
      const response = await apiClient.post('/address', normalized);
      const row = unwrapAddressEntity(response.data);
      return mapApiAddressToState(row);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to create address');
    }
  }
);

// Async thunk for updating an address
export const updateAddressApi = createAsyncThunk<
  Address,
  { id: string } & {
    address_line_1: string;
    user_id: string;
    address_line_2?: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
    address_type: string;
    location: { lat: string; long: string };
    name: string;
    phoneNumber: string;
  },
  { rejectValue: string }
>(
  'address/updateAddress',
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const normalized = {
        ...payload,
        city: resolveAddressCity({
          city: payload.city,
          address_line_1: payload.address_line_1,
        }) || payload.city,
        address_line_2: payload.address_line_2 != null && String(payload.address_line_2).trim() ? payload.address_line_2 : '-',
        state: payload.state != null && String(payload.state).trim() ? payload.state : '-',
      };
      const response = await apiClient.patch(`/address/${id}`, normalized);
      const row = unwrapAddressEntity(response.data);
      return mapApiAddressToState(row);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to update address');
    }
  }
);

// Async thunk for deleting an address
export const deleteAddress = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'address/deleteAddress',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/address/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to delete address');
    }
  }
);

// Async thunk for fetching addresses
export const fetchAddresses = createAsyncThunk<
  Address[],
  string,
  { rejectValue: string }
>(
  'address/fetchAddresses',
  async (user_id, { rejectWithValue }) => {
    try {
      const uid = String(user_id ?? '').trim();
      if (!uid || uid === 'user_id') {
        return rejectWithValue('User not signed in');
      }
      const response = await apiClient.get(`/address/${uid}`);
      const body = response.data;
      if (body && typeof body === 'object' && body.success === false) {
        return rejectWithValue(body.message || 'Failed to fetch addresses');
      }
      const list = unwrapAddressList(body);
      return list.map((row) => mapApiAddressToState(row));
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch addresses');
    }
  }
);

const addressSlice = createSlice({
  name: 'address',
  initialState,
  reducers: {
    setAddresses: (state, action: PayloadAction<Address[]>) => {
      state.addresses = action.payload;
      state.loading = false;
      state.error = null;
    },
    addAddress: (state, action: PayloadAction<Address>) => {
      state.addresses.push(action.payload);
    },
    updateAddress: (state, action: PayloadAction<Address>) => {
      const idx = state.addresses.findIndex(a => a.id === action.payload.id);
      if (idx !== -1) {
        state.addresses[idx] = action.payload;
      }
    },
    removeAddress: (state, action: PayloadAction<string>) => {
      state.addresses = state.addresses.filter(a => a.id !== action.payload);
      if (state.selectedAddressId === action.payload) {
        state.selectedAddressId = state.addresses.length > 0 ? state.addresses[0].id : null;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetAddresses: (state) => {
      state.addresses = [];
      state.selectedAddressId = null;
      state.loading = false;
      state.error = null;
    },
    setSelectedAddressId: (state, action: PayloadAction<string | null>) => {
      state.selectedAddressId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Ensure we're adding the transformed address
        state.addresses.push(action.payload);
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to create address';
      })
      .addCase(updateAddressApi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAddressApi.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const idx = state.addresses.findIndex(a => a.id === action.payload.id);
        if (idx !== -1) {
          state.addresses[idx] = action.payload;
        } else {
          // If address not found, add it (edge case)
          state.addresses.push(action.payload);
        }
      })
      .addCase(updateAddressApi.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to update address';
      })
      .addCase(fetchAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.addresses = action.payload;
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch addresses';
        // Keep existing addresses on fetch failure so the list does not disappear.
      })
      .addCase(deleteAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.addresses = state.addresses.filter(a => a.id !== action.payload);
        if (state.selectedAddressId === action.payload) {
          state.selectedAddressId = state.addresses.length > 0 ? state.addresses[0].id : null;
        }
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to delete address';
      });
  },
});

export const {
  setAddresses,
  addAddress,
  updateAddress,
  removeAddress,
  setLoading,
  setError,
  resetAddresses,
  setSelectedAddressId,
} = addressSlice.actions;

export default addressSlice.reducer;

// Selectors
export const selectAddresses = (state: RootState) => state.address.addresses;
export const selectSelectedAddressId = (state: RootState) => state.address.selectedAddressId;
export const selectAddressLoading = (state: RootState) => state.address.loading;
export const selectAddressError = (state: RootState) => state.address.error;
